import React from "react";

import "./groupUsers.scss";

import { type EnrollmentRule } from "features/People/Groups/AddEditGroupForms/EnrollmentBuilder/utils";
import { type GetGroupEnrollmentUsersPreviewRequest, type GroupUsersState, type UserInfo } from "features/People/types";
import { type FiltersMap } from "utils/filterUtils";
import { ValidatedForm } from "../../../../../components/forms";
import validationSchemas from "../../../../../utils/validationSchemas";
import AutomaticallyDetect from "../../AddEditGroupForms/AutomaticallyDetect/AutomaticallyDetect";
import ManuallySelect from "./ManuallySelect/ManuallySelect";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";
import ManageMembers from "./ManageMembers/ManageMembers";

type UserOptions = {
  items: UserInfo[];
  itemsCount: number;
  isLoading: boolean;
  appliedFilter?: FiltersMap;
};

type Props = {
  groupId: number;
  addMembersBtn: React.ReactNode;
  groupUsers: {
    enrollmentRules: EnrollmentRule[];
    userIds: number[];
  };
  enrollmentUsersOptions: UserOptions;
  loadEnrollmentUsers: (
    skip: number,
    top: number,
    orderByParams: string,
    filterParams: FiltersMap,
  ) => (dispatch: Function) => Promise<void>;
  resetEnrollmentUsers: () => void;
  setAutomaticEnrollmentGridFilter: () => void;
  resetAutomaticEnrollmentGridFilter: () => void;

  manualSelectionUsersOptions: UserOptions;
  loadManualSelectionUsers: (
    skip: number,
    top: number,
    orderByParams: string,
    filterParams: FiltersMap,
    search: string,
  ) => (dispatch: Function) => Promise<void>;

  setUserFilter: (filter: FiltersMap) => void;
  resetUsersFilter: () => void;
  isWithAutoEnroll: boolean;
  bindSubmitForm: (submitForm: () => void) => void;
  onSubmit: (groupUsers: GroupUsersState) => Promise<void>;
  onIsValidChange?: (isValid: boolean) => void;
  updateSelectedItems?: (items: any[]) => void;
  search?: string;
  onSearchChanged?: (search: string) => void;
  setReloadListItems?: (reloadListItems: () => void) => void;
  isInitialValid?: boolean;
  fetchGroupEnrollmentUsers: (req: GetGroupEnrollmentUsersPreviewRequest) => void;
  groupEnrollmentUsers: UserOptions;
  bindGetDraftEnrollmentRulesValues: (getDraftEnrollmentRules: () => GroupUsersState) => void;
  usersFilterOptions: FiltersMap;
  usersAppliedFilter: FiltersMap;
  getFilterOptions: () => void;
};

const GroupUsers = ({
  groupId,
  addMembersBtn,
  manualSelectionUsersOptions,
  loadManualSelectionUsers,
  setUserFilter,
  resetUsersFilter,
  groupUsers,
  updateSelectedItems,
  search,
  onSearchChanged,
  setReloadListItems,
  isWithAutoEnroll,
  onSubmit,
  bindSubmitForm,
  isInitialValid,
  bindGetDraftEnrollmentRulesValues,
  onIsValidChange,
  groupEnrollmentUsers,
  enrollmentUsersOptions,
  loadEnrollmentUsers,
  setAutomaticEnrollmentGridFilter,
  fetchGroupEnrollmentUsers,
  resetEnrollmentUsers,
  getFilterOptions,
  usersFilterOptions,
  usersAppliedFilter,
}: Props) => {
  const isGroupCreateCsvUserImportEnabled = useFeatureFlag(FeatureFlags.GroupCreateCsvUserImport);

  const manageMembersContent = isGroupCreateCsvUserImportEnabled ? (
    <ManageMembers groupId={groupId} addMembersBtn={addMembersBtn} />
  ) : (
    <ManuallySelect
      usersList={manualSelectionUsersOptions}
      loadUsers={loadManualSelectionUsers}
      setUserFilter={setUserFilter}
      resetUserFilter={resetUsersFilter}
      updateSelectedItems={updateSelectedItems}
      selectedItemIds={groupUsers.userIds}
      usersFilterOptions={usersFilterOptions}
      usersAppliedFilter={usersAppliedFilter}
      getFilterOptions={getFilterOptions}
      setReloadListItems={setReloadListItems}
      search={search}
      onSearchChanged={onSearchChanged}
    />
  );
  return (
    <section className="group-users">
      <div className="stretch">
        {isWithAutoEnroll ? (
          <ValidatedForm
            onSubmit={onSubmit}
            bindGetValues={bindGetDraftEnrollmentRulesValues}
            bindSubmitForm={bindSubmitForm}
            isInitialValid={isInitialValid}
            initialValues={groupUsers}
            validationSchema={validationSchemas.automaticEnrollment}
            unsavedChangesPrompt={{
              title: "Exit Without Saving?",
              message: "Are you sure you want to exit without saving this group? All information entered will be lost.",
            }}
            formWidthComputer={16}
          >
            {
              /* instanbul ignore next */
              (formProps) => {
                const { values, validatedFieldProps } = formProps;

                return (
                  <AutomaticallyDetect
                    onIsValidChange={onIsValidChange}
                    values={values}
                    validatedFieldProps={validatedFieldProps}
                    users={groupEnrollmentUsers.items}
                    usersCount={groupEnrollmentUsers.itemsCount}
                    isLoading={groupEnrollmentUsers.isLoading}
                    loadUsers={loadEnrollmentUsers}
                    gridFilter={enrollmentUsersOptions.appliedFilter || {}}
                    setGridFilter={setAutomaticEnrollmentGridFilter}
                    resetEnrollmentUsers={resetEnrollmentUsers}
                    fetchGroupEnrollmentUsers={fetchGroupEnrollmentUsers}
                  />
                );
              }
            }
          </ValidatedForm>
        ) : (
          manageMembersContent
        )}
      </div>
    </section>
  );
};

export default GroupUsers;
